import { useEffect, useRef, useState } from "react";
import { VolumeX, Volume2 } from "lucide-react";
import { useSite } from "@/lib/useSite";
import { ytId } from "@/lib/api";
import Chapter from "@/components/Chapter";

let ytReadyPromise;
function loadYT() {
  if (!ytReadyPromise) {
    ytReadyPromise = new Promise((resolve) => {
      if (window.YT && window.YT.Player) return resolve();
      const tag = document.createElement("script");
      tag.src = "https://www.youtube.com/iframe_api";
      document.head.appendChild(tag);
      const prev = window.onYouTubeIframeAPIReady;
      window.onYouTubeIframeAPIReady = () => {
        if (prev) prev();
        resolve();
      };
    });
  }
  return ytReadyPromise;
}

export default function VideoWall() {
  const { data, isLoading } = useSite();
  const videos = (data?.videos || []).filter((v) => ytId(v.url));
  const ids = videos.map((v) => ytId(v.url));
  const idsKey = ids.join(",");

  // 1. Declare ALL Hooks first
  const containerRef = useRef(null);
  const playerRef = useRef(null);
  const [active, setActive] = useState(0);
  const [muted, setMuted] = useState(true);
  const [progress, setProgress] = useState(0);
  const activeRef = useRef(0);
  const mutedRef = useRef(true);

  activeRef.current = active;
  mutedRef.current = muted;

  // 2. All useEffect hooks
  useEffect(() => {
    if (!ids.length) return;
    if (playerRef.current && playerRef.current.loadVideoById) {
      playerRef.current.loadVideoById(ids[0]);
      setActive(0);
      activeRef.current = 0;
      return;
    }
    loadYT().then(() => {
      if (playerRef.current || !containerRef.current) return;
      playerRef.current = new window.YT.Player(containerRef.current, {
        videoId: ids[0],
        playerVars: {
          autoplay: 1,
          mute: 1,
          controls: 0,
          rel: 0,
          playsinline: 1,
          modestbranding: 1,
          iv_load_policy: 3,
          disablekb: 1,
          fs: 0,
        },
        events: {
          onReady: (e) => {
            e.target.mute();
            e.target.playVideo();
          },
          onStateChange: (e) => {
            if (e.data === window.YT.PlayerState.ENDED) {
              const list = idsKey.split(",");
              const next = (activeRef.current + 1) % list.length;
              setActive(next);
              activeRef.current = next;
              e.target.loadVideoById(list[next]);
              if (mutedRef.current) e.target.mute();
            }
          },
          onError: () => {
            const list = idsKey.split(",");
            if (list.length > 1) {
              const next = (activeRef.current + 1) % list.length;
              setActive(next);
              activeRef.current = next;
              playerRef.current?.loadVideoById(list[next]);
            }
          },
        },
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idsKey]);

  useEffect(
    () => () => {
      try {
        playerRef.current?.destroy?.();
      } catch (e) {}
      playerRef.current = null;
    },
    []
  );

  useEffect(() => {
    const t = setInterval(() => {
      const p = playerRef.current;
      if (p && p.getDuration && p.getDuration() > 0) {
        setProgress(p.getCurrentTime() / p.getDuration());
      }
    }, 500);
    return () => clearInterval(t);
  }, []);

  // 3. Helper Functions
  const jump = (i) => {
    setActive(i);
    activeRef.current = i;
    setProgress(0);
    const p = playerRef.current;
    if (p && p.loadVideoById) {
      p.loadVideoById(ids[i]);
      if (mutedRef.current) p.mute();
    }
  };

  const toggleMute = () => {
    const p = playerRef.current;
    if (!p) return;
    if (mutedRef.current) {
      p.unMute();
      p.setVolume(90);
      setMuted(false);
    } else {
      p.mute();
      setMuted(true);
    }
  };

  // 4. Conditional Early Return (Placed AFTER hooks so Rules of Hooks pass)
  if (isLoading || ids.length === 0) {
    return null;
  }

  // 5. Main Component Render
  return (
    <div>
      <Chapter
        no="04"
        kicker="Customer Stories"
        heading="Heard on site."
        sub="Real customers, real roofs — in their own words."
      />
      <div data-testid="video-wall" className="flex flex-col items-center gap-10 lg:flex-row lg:items-start lg:gap-14">
        <div className="relative w-full max-w-[380px] shrink-0">
          <div className="relative aspect-[9/16] w-full overflow-hidden rounded-[2.5rem] bg-ink shadow-2xl shadow-ink/25">
            <div ref={containerRef} className="absolute inset-0 h-full w-full" data-testid="video-player-iframe" />
            <div className="absolute right-4 top-4 z-10 rounded-full bg-ink/60 px-3.5 py-1.5 font-mono text-[11px] tracking-[0.25em] text-white/90 backdrop-blur-sm">
              {String(active + 1).padStart(2, "0")} / {String(ids.length).padStart(2, "0")}
            </div>
          </div>
          <button
            data-testid="video-unmute-button"
            onClick={toggleMute}
            className="absolute bottom-5 left-1/2 z-10 flex -translate-x-1/2 items-center gap-2.5 whitespace-nowrap rounded-full bg-white px-6 py-3 font-mono text-[11px] uppercase tracking-[0.2em] text-ink shadow-lg transition-colors hover:bg-srs hover:text-white"
          >
            {muted ? <VolumeX size={15} /> : <Volume2 size={15} />}
            {muted ? "Tap for sound" : "Sound on"}
          </button>
        </div>

        <div className="w-full min-w-0 flex-1">
          <p className="mb-4 font-mono text-[11px] uppercase tracking-[0.25em] text-subtle">
            Playlist · {ids.length} review{ids.length === 1 ? "" : "s"} · plays in sequence
          </p>
          <div className="flex gap-1.5">
            {ids.map((_, i) => (
              <div key={i} className="relative h-1.5 flex-1 overflow-hidden rounded-full bg-ink/10">
                {i < active && <div className="absolute inset-0 rounded-full bg-ink/30" />}
                {i === active && (
                  <div className="absolute inset-y-0 left-0 rounded-full bg-srs transition-[width] duration-500 ease-linear" style={{ width: `${progress * 100}%` }} />
                )}
              </div>
            ))}
          </div>

          <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-3">
            {videos.map((v, i) => (
              <button
                key={v.id || i}
                data-testid={`video-thumbnail-${i}`}
                onClick={() => jump(i)}
                className={`group relative shrink-0 overflow-hidden rounded-2xl text-left shadow-[0_12px_30px_-16px_rgba(13,13,17,0.25)] transition-all duration-300 hover:shadow-[0_20px_45px_-18px_rgba(13,13,17,0.38)] ${
                  i === active ? "ring-2 ring-srs ring-offset-2" : "opacity-55 hover:opacity-100"
                }`}
              >
                <img
                  src={`https://i.ytimg.com/vi/${ids[i]}/mqdefault.jpg`}
                  alt={v.title || `Customer review ${i + 1}`}
                  loading="lazy"
                  className="aspect-video w-full rounded-2xl object-cover"
                />
                <span className="mt-2 block truncate font-mono text-[10px] uppercase tracking-[0.18em] text-mute">
                  {v.title || `Review ${String(i + 1).padStart(2, "0")}`}
                </span>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
